import React, { FC, useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import FileArrowUp from '@gravity-ui/icons/FileArrowUp';
import { Icon, Loader } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import { AliceAgentAppImageDTO, uploadAliceAgentAppLogo } from 'client/api/services/aliceAgentApps';
import { ACCEPTED_LOGO_FORMATS } from 'client/utils/settings/common';
import * as keyset from 'i18n/AliceAgentAppForm';

import styles from './AliceAgentAppLogoUploader.module.scss';

const t = i18n(keyset);

interface Props {
    skillId: string;
    logoUrl?: string | null;
    disabled: boolean;
    onUploaded: (image: AliceAgentAppImageDTO) => void;
    onError: (message: string) => void;
}

const renderIcon = (logoUrl: string, isLoading: boolean, isDragActive: boolean, disabled: boolean) => {
    if (isLoading) {
        return (
            <div className={clx(styles.iconContainer, styles.iconLoading)}>
                <Loader size="s" />
            </div>
        );
    }
    if (isDragActive) {
        return (
            <div className={clx(styles.iconContainer, styles.iconEmpty)}>
                <div>{t('upload-drop-here')}</div>
            </div>
        );
    }
    if (logoUrl) {
        return (
            <>
                <img className={styles.iconShadow} src={logoUrl} alt="" />
                <div
                    className={clx(styles.iconContainer, styles.iconPreview, disabled && styles.iconContainer_disabled)}
                    style={{ backgroundImage: `url(${logoUrl})` }}
                />
            </>
        );
    }
    return (
        <div className={clx(styles.iconContainer, styles.iconEmpty, disabled && styles.iconContainer_disabled)}>
            <Icon data={FileArrowUp} size={15} />
            <div className={styles.iconResolution}>360x360</div>
            <div className={styles.iconLabel}>{t('upload-label')}</div>
        </div>
    );
};

export const AliceAgentAppLogoUploader: FC<Props> = ({ skillId, logoUrl, disabled, onUploaded, onError }) => {
    const [isLoading, setIsLoading] = useState(false);

    const onDrop = useCallback(
        async (acceptedFiles: File[]) => {
            if (!acceptedFiles.length) {
                onError(t('upload-format-error'));
                return;
            }
            setIsLoading(true);
            try {
                const image = await uploadAliceAgentAppLogo(skillId, acceptedFiles[0]);
                onUploaded(image);
            } catch (err) {
                const message =
                    err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
                        ? err.message
                        : t('upload-error');
                onError(message);
            } finally {
                setIsLoading(false);
            }
        },
        [skillId, onUploaded, onError],
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        disabled: disabled || isLoading,
        accept: ACCEPTED_LOGO_FORMATS,
        multiple: false,
    });

    return (
        <div
            className={clx(styles.uploaderContainer, (disabled || isLoading) && styles.uploaderContainer_disabled)}
            {...getRootProps()}
        >
            <input {...getInputProps()} />
            {renderIcon(logoUrl || '', isLoading, isDragActive, disabled)}
        </div>
    );
};
